// Batch 29 · M64 (c) — the answer "Recorded at year end (accrual / other)" on a row of the unrecorded-liabilities worksheet
// (route 1 payments / invoices and route 2 subsequent journals): the auditor names the voucher that recorded the liability at
// the year end (required) → the row is "Recorded", nothing omitted. "Take back" clears the answer. Own file (big-file rule).
// Batch 31 · item 9 (M91): the answer may name several vouchers (comma separated); route 1 then lists the GL entries found,
// their total and whether it agrees to the row. SurlEntries also lists a multi-entry auto-match.
import React from "react";
import { askPrompt } from "@/lib/appDialog";
import { fmtNumber } from "@/components/Amount";
import { fmtDate } from "@/lib/dt";

export const RYE_TEXT = {
  en: { answer: "Recorded at year end (accrual / other)…",
        ask: "Which voucher(s) recorded this liability at the year end? (one voucher number, or several separated by commas)",
        recorded: "Recorded at year end", by: "by", undo: "Take back",
        entries: "Matched to {n} entries", total: "Total", agrees: "agrees to the row", differs: "does not agree to the row", notFound: "Not in the general ledger of this head" },
  ar: { answer: "مسجل في نهاية السنة (استحقاق / غير ذلك)…",
        ask: "ما القيد أو القيود التي سجّلت هذا الالتزام في نهاية السنة؟ (رقم قيد واحد، أو عدة أرقام تفصل بينها فواصل)",
        recorded: "مسجل في نهاية السنة", by: "بواسطة", undo: "تراجع",
        entries: "مطابق مع {n} قيود", total: "الإجمالي", agrees: "يتفق مع الصف", differs: "لا يتفق مع الصف", notFound: "غير موجود في دفتر الأستاذ العام لهذا البند" },
};

const fmt = (n) => fmtNumber(Number(n || 0));

/** Batch 31 · item 9 (M91): the GL entries behind one row — voucher · date · amount, then the total. */
export function SurlEntries({ entries, total, agrees, notFound, lang = "en", testid }) {
  const T = RYE_TEXT[lang === "ar" ? "ar" : "en"];
  const list = Array.isArray(entries) ? entries : [];
  if (list.length < 2 && total === undefined) return null;
  return (
    <div className="mt-1 text-[11px]" data-testid={testid}>
      <div>{T.entries.replace("{n}", String(list.length))}</div>
      <ul className="ms-3 list-disc">
        {list.map((e, i) => (
          <li key={`${e.voucher}-${i}`}>{e.voucher || "—"} · {fmtDate(e.date)} · {fmt(e.amount)}{e.account_code ? ` · ${e.account_code}` : ""}</li>
        ))}
      </ul>
      {total !== undefined && (
        <div className={agrees === false ? "text-red-700" : ""} data-testid={`${testid}-total`}>
          {T.total}: {fmt(total)}{agrees === undefined ? "" : ` — ${agrees ? T.agrees : T.differs}`}
        </div>)}
      {Array.isArray(notFound) && notFound.length > 0 &&
        <div className="text-red-700" data-testid={`${testid}-missing`}>{T.notFound}: {notFound.join(", ")}</div>}
    </div>
  );
}

/** ref = the row's current answer (draft or saved); onAnswer(ref | "") writes or drafts it. Shown on a selected row that is
 *  omitted / to confirm, or already answered. */
export default function RecordedYe({ ev, ref_, onAnswer, lang = "en", disabled = false, testid }) {
  const T = RYE_TEXT[lang === "ar" ? "ar" : "en"];
  const e = ev || {};
  const current = (ref_ || "").trim();
  if (current) {
    const rye = e.recorded_ye || {};
    const several = Array.isArray(rye.refs) && rye.refs.length > 1 && rye.total !== undefined;
    return (
      <div className="mt-1 text-[11px] text-emerald-800" data-testid={`${testid}-done`}>
        {T.recorded}: <b>{current}</b>
        {!disabled && <button type="button" className="ms-1 underline" onClick={() => onAnswer("")} data-testid={`${testid}-undo`}>{T.undo}</button>}
        {several && <SurlEntries entries={rye.entries} total={rye.total} agrees={rye.agrees} notFound={rye.not_found} lang={lang} testid={`${testid}-entries`} />}
      </div>
    );
  }
  if (!e.selected || !["omitted", "part_omitted", "to_confirm"].includes(e.result)) return null;
  return (
    <button type="button" disabled={disabled} className="mt-1 block text-[11px] underline" data-testid={testid}
            onClick={async () => { const v = await askPrompt(T.ask, ""); if (v && v.trim()) onAnswer(v.trim()); }}>{T.answer}</button>
  );
}
